import { type Dispatch, type SetStateAction, useCallback } from "react";
import type { HourRange } from "../../../../components/UIToolkit/Inputs/TimeRangeListField";
import type { TokenListCommit } from "../../../../components/UIToolkit/Inputs/TokenListField";
import { parsePartialShiftHourEntries } from "../utils";
import type { GeneralFormData } from "./types";

/**
 * Which map a row writes. `role` writes the legacy role-keyed maps (`partialBlockShifts` /
 * `fullShiftTimes`, plus the mode-independent `default` bucket); `job` writes the per-job slice
 * (`jobShiftTimes`, DAT-580).
 *
 * The two must never cross — a job id landing in `partialBlockShifts` would be persisted into the
 * department's role-keyed column, where the time picker can never find it again.
 */
export type ShiftTimesScope = "role" | "job";

type ShiftTimesPatch = Partial<{ partialBlockShifts: number[]; fullShiftTimes: HourRange[] }>;

const sameHours = (a: number[], b: number[]): boolean => a.length === b.length && a.every((value, i) => value === b[i]);

/** Applies one row's patch to whichever map its scope owns, leaving the other untouched. */
const writeShiftTimes = (prev: GeneralFormData, scope: ShiftTimesScope, key: string, patch: ShiftTimesPatch): GeneralFormData => {
	if (scope === "job") {
		const current = prev.jobShiftTimes[key] ?? { partialBlockShifts: [], fullShiftTimes: [] };
		return { ...prev, jobShiftTimes: { ...prev.jobShiftTimes, [key]: { ...current, ...patch } } };
	}
	let next = prev;
	if (patch.partialBlockShifts) next = { ...next, partialBlockShifts: { ...next.partialBlockShifts, [key]: patch.partialBlockShifts } };
	if (patch.fullShiftTimes) next = { ...next, fullShiftTimes: { ...next.fullShiftTimes, [key]: patch.fullShiftTimes } };
	return next;
};

/**
 * The two writers one shift-times editor needs, whatever surface it is drawn on — the role-keyed
 * table row, the mode-independent `default` bucket, or the per-job panel inside the Jobs grid.
 *
 * Shared rather than duplicated because the scope rule above is the one thing that must never drift
 * between them, and because both surfaces owe the same "an unreadable entry changes nothing" guard.
 */
export const useShiftTimesWriters = ({
	scope,
	rowKey,
	hours,
	setFormData,
}: {
	scope: ShiftTimesScope;
	/** Role short code (or `default`) in role scope, job id in job scope. */
	rowKey: string;
	/** Partial lengths currently configured, so a re-parse that changes nothing doesn't dirty the form. */
	hours: number[];
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
}): { commitPartialBlockShifts: TokenListCommit; setFullShiftTimes: (next: HourRange[]) => void } => {
	const commitPartialBlockShifts = useCallback<TokenListCommit>(
		(entries) => {
			const { values, rejected } = parsePartialShiftHourEntries(entries);
			// An unreadable entry changes nothing, so don't mark the whole form dirty over a typo.
			if (!sameHours(values, hours)) setFormData((prev) => writeShiftTimes(prev, scope, rowKey, { partialBlockShifts: values }));
			return rejected;
		},
		[scope, rowKey, hours, setFormData],
	);

	// The picker can only produce whole valid ranges, so this is a straight write — no parsing, and no
	// "did anything actually change" guard: the field only fires on an add or a remove.
	const setFullShiftTimes = useCallback(
		(next: HourRange[]) => setFormData((prev) => writeShiftTimes(prev, scope, rowKey, { fullShiftTimes: next })),
		[scope, rowKey, setFormData],
	);

	return { commitPartialBlockShifts, setFullShiftTimes };
};

/**
 * Partial lengths already configured anywhere in this department, offered as pick-list options on
 * every row — that field is free text, so a department gets a suggestion list instead of an input
 * that suggests nothing (DAT-728: "shift times are not popping up"). Full shift times need no
 * equivalent: they are picked, not typed.
 *
 * Both maps are read regardless of mode, so a department that switches to job mode still sees the
 * lengths its roles were configured with.
 */
export const collectPartialHourOptions = (
	jobShiftTimes: GeneralFormData["jobShiftTimes"],
	partialBlockShifts: GeneralFormData["partialBlockShifts"],
): string[] => {
	const hours = new Set<number>();
	const collect = (values: number[] | undefined) => {
		for (const value of values ?? []) hours.add(value);
	};
	for (const row of Object.values(jobShiftTimes)) collect(row.partialBlockShifts);
	for (const values of Object.values(partialBlockShifts)) collect(values);
	return [...hours].sort((a, b) => a - b).map(String);
};
